import type { BikeStatus } from "../bikes/bikes.schema.ts";
import type { MaterialShippedQuantityEffect } from "../materials/materials-shipping.schema.ts";
import type { UserRole } from "../auth/auth.schema.ts";
import { WELL_KNOWN_IDS } from "../common/constants.ts";

/** An FSM entity type (e.g. User, Role, System). Well-known ID is optional — omit to auto-generate. */
export interface PresetEntityType {
  id?: string;
  name: string;
  description?: string;
}

export interface PresetUser {
  email: string;
  name: string;
  password: string;
  roles: UserRole[];
  customers: string[];
  /** Location keys to assign this user to. Omit for no assignment (sees all locations). */
  locations?: string[];
  /** CASL customer_role name to assign on the primary customer membership (e.g. "solo-lectura"). */
  customerRole?: string;
}

export interface PresetCustomer {
  id?: string;
  name: string;
  slug: string;
  /** Marks this customer as owning shared system data (see `adr/035`). Only the `foundation` preset's root customer sets this. */
  isSystem?: boolean;
}

/** A named FSM role entity in a customer, with explicit user assignments. */
export interface PresetEntityRole {
  name: string;
  customer: string;
  /** RBAC permissions to seed into role_permissions for the matching customer_roles entry. */
  rbacPermissions?: { action: string; subject: string }[];
  /** Doctype-scoped RBAC rules. documentTypeKey refs a PresetDocumentType key in the same customer. */
  documentTypeRules?: { documentTypeKey: string; level: "read" | "write" }[];
  /** Base emails (pre-namespace) of users to assign this role to. */
  assignTo: string[];
}

/** A document type definition within a preset. `customer` refs a PresetCustomer slug. */
export interface PresetDocumentType {
  id?: string;
  key: string;
  name: string;
  description?: string;
  customer: string;
  translations?: { en?: string; es?: string };
  displayOrder?: number;
}

/** A workflow state. `documentType` refs a PresetDocumentType key. */
export interface PresetState {
  id?: string;
  key: string;
  name: string;
  documentType: string;
  isInitial?: boolean;
  isFinal?: boolean;
  description?: string;
  notifyEntityIds?: string[];
  translations?: { en?: string; es?: string };
}

/** A transition between states. `executors` are role entity names (match auth user roles). */
export interface PresetTransition {
  id?: string;
  key: string;
  name: string;
  documentType: string;
  from: string;
  to: string;
  executors: string[];
  /** If set, the engine treats this as a self-loop valid from any state. */
  transitionType?: "admin_edit";
  /** If set, executing this transition automatically creates a child document of the referenced type. */
  triggerDocumentTypeKey?: string;
  translations?: { en?: string; es?: string };
}

export interface PresetSelectOption {
  value: string;
  label: string;
}

/** A field definition on a document type. */
export interface PresetFieldDefinition {
  id?: string;
  documentType: string;
  name: string;
  label: string;
  fieldType:
    | "text"
    | "number"
    | "email"
    | "date"
    | "select"
    | "textarea"
    | "boolean"
    | "multi_select"
    | "file"
    | "material_picker"
    | "url";
  isRequired?: boolean;
  sortOrder?: number;
  options?: PresetSelectOption[];
  validation?: Record<string, unknown>;
  translations?: { en?: string; es?: string };
}

/** Links required fields to a specific transition (by key). */
export interface PresetTransitionRequiredFields {
  transition: string;
  fields: string[];
}

/** Configures the bike status side-effect for a specific transition (by key). */
export interface PresetTransitionBikeEffect {
  transition: string;
  bikeStatus: BikeStatus;
}

/** Declares what a transition does to `shipped_quantity` of the request's
 *  material lines (BEL-646). Keyed by transition key, seeded into
 *  `core_transition_material_effects`. A transition with no entry here never
 *  touches `shipped_quantity` — that is how `mat-cancel` stays inert. */
export interface PresetTransitionMaterialEffect {
  transition: string;
  shippedQuantityEffect: MaterialShippedQuantityEffect;
}

/** Restricts a field to only be visible during specific transitions (by key).
 *  If no entry exists for a field, it is visible in all transitions. */
export interface PresetTransitionVisibleFields {
  field: string;
  transitions: string[];
}

/** A pre-seeded document instance. `state` refs a PresetState key. */
export interface PresetDocument {
  name: string;
  documentType: string;
  state: string;
  customer: string;
}

/** A pre-seeded location. `customer` refs a PresetCustomer slug. */
export interface PresetLocation {
  key: string;
  name: string;
  city: string;
  customer: string;
  address?: string;
  postalCode?: string;
  country?: string;
  coordinates?: { lat: number; lng: number };
  locationType?: "client" | "depot";
  /**
   * Email for an anonymous user assigned to this location.
   * Used by the public repair form to identify the requester.
   * If omitted, no anon user is created.
   */
  anonUserEmail?: string;
  /** Short code used in /r/:code QR redirect URLs. Must be globally unique. */
  repairCode?: string;
  /**
   * Document type keys available at this location.
   * If omitted, no entries are inserted into core_location_document_types for this location.
   */
  allowedDocumentTypeKeys?: string[];
}

/** A pre-seeded bike model. `customer` refs a PresetCustomer slug. */
export interface PresetBikeModel {
  key: string;
  model: string;
  submodel?: string;
  code: string;
  customer: string;
}

/** A pre-seeded bike instance. `model` refs a PresetBikeModel key, `location` refs a PresetLocation key. */
export interface PresetBike {
  model: string;
  location: string;
  serialNumber?: string;
  beloraBikeId?: string;
  clientBaseNumber?: string;
  color?: string;
  status?: string;
}

/** A pre-seeded material catalog entry. `bikeModels` refs PresetBikeModel keys for compatibility. */
export interface PresetMaterial {
  sku: string;
  name: string;
  unit: string | null;
  category: string;
  clientCost: number;
  bikeModels: string[];
  customer: string;
  isConsumable?: boolean;
}

export interface PresetData {
  users: PresetUser[];
  customers: PresetCustomer[];
  entityTypes?: PresetEntityType[];
  locations?: PresetLocation[];
  bikeModels?: PresetBikeModel[];
  bikes?: PresetBike[];
  materials?: PresetMaterial[];
  entityRoles?: PresetEntityRole[];
  documentTypes?: PresetDocumentType[];
  states?: PresetState[];
  transitions?: PresetTransition[];
  fieldDefinitions?: PresetFieldDefinition[];
  transitionRequiredFields?: PresetTransitionRequiredFields[];
  transitionBikeEffects?: PresetTransitionBikeEffect[];
  transitionMaterialEffects?: PresetTransitionMaterialEffect[];
  transitionVisibleFields?: PresetTransitionVisibleFields[];
  documents?: PresetDocument[];
}

const ROOT_PASS =
  "*_EzuKFfmPkDH37n2WR27KQyWPzV*@goHobiEnwUrr6FnbmdedoH-Xdu.cF8DD6*";

export const PRESET_DATA: Record<string, PresetData> = {
  /**
   * Foundation — must run before any client preset in every environment.
   * Creates the structural entities the app requires to function:
   *   - Root customer (well-known UUID)
   *   - FSM entity types: User, Role, System
   *   - Default role entities: root, registered
   *   - Root admin user (i@d13z.dev)
   */
  foundation: {
    customers: [
      {
        id: WELL_KNOWN_IDS.SYSTEM_CUSTOMER_ID,
        name: "Belora",
        slug: "customer-root",
        isSystem: true,
      },
    ],

    entityTypes: [
      {
        id: WELL_KNOWN_IDS.ET_USER_ID,
        name: "User",
        description: "Individual human user",
      },
      {
        id: WELL_KNOWN_IDS.ET_ROLE_ID,
        name: "Role",
        description: "System role for permissions",
      },
      {
        id: WELL_KNOWN_IDS.ET_SYSTEM_ID,
        name: "System",
        description: "Automated system or external API actor",
      },
    ],

    users: [
      {
        email: "i@d13z.dev",
        name: "Danilo Velasquez",
        password: ROOT_PASS,
        roles: ["root"],
        customers: ["customer-root"],
      },
    ],

    entityRoles: [
      {
        name: "root",
        customer: "customer-root",
        assignTo: ["i@d13z.dev"],
      },
      {
        name: "registered",
        customer: "customer-root",
        assignTo: [],
      },
    ],
  },

  minimal: {
    users: [
      {
        email: "root@example.com",
        name: "Root Preset",
        password: "testing123456",
        roles: ["root"],
        customers: ["test-customer"],
      },
      {
        email: "danilo@example.com",
        name: "Danilo Preset",
        password: "testing123456",
        roles: ["root"],
        customers: ["test-customer"],
      },
      {
        email: "admin@example.com",
        name: "Admin Preset",
        password: "testing123456",
        roles: ["root"],
        customers: ["test-customer"],
      },
      {
        email: "user@example.com",
        name: "User Preset",
        password: "testing123456",
        roles: ["registered"],
        customers: ["test-customer"],
      },
      {
        // BEL-765: a platform-admin with NO customer memberships — the shape
        // that exposed `authorizedTenants.find(...)` returning `undefined`
        // for a customer-scoped screen. `roles: ["root"]` grants the
        // `platform_admin` column (see dev.service.ts); `customers: []`
        // deliberately leaves it with zero memberships.
        email: "platform-admin@example.com",
        name: "Platform Admin Preset",
        password: "testing123456",
        roles: ["root"],
        customers: [],
      },
    ],
    customers: [{ name: "Test Customer", slug: "test-customer" }],
    entityRoles: [
      {
        name: "root",
        customer: "test-customer",
        assignTo: [
          "root@example.com",
          "danilo@example.com",
          "admin@example.com",
        ],
      },
      {
        name: "registered",
        customer: "test-customer",
        assignTo: ["user@example.com"],
      },
      {
        // BEL-454: fixture role with no permissions, for tests that need an
        // isolated role entity to exercise the RoleForm UI (e.g.
        // role-module-permissions.e2e.ts). Role entities can no longer be
        // created ad hoc via the generic entity-admin endpoint — this preset
        // role is seeded via the authorized seedEntityRoles() path instead.
        name: "custom-role",
        customer: "test-customer",
        assignTo: [],
      },
    ],
    locations: [
      {
        key: "test-location-1",
        name: "Test Location 1",
        city: "Madrid",
        customer: "test-customer",
        address: "Calle Falsa 123",
      },
    ],
    // BEL-716: minimal set of catalog/FSM/document content so CI's
    // "Capture design screens" step (ci.yml) doesn't produce empty admin
    // screenshots — this preset replaced `belora` as that step's seed source.
    documentTypes: [
      { key: "GEN", name: "General Request", customer: "test-customer" },
    ],
    states: [
      {
        key: "requested",
        name: "Requested",
        documentType: "GEN",
        isInitial: true,
      },
      { key: "done", name: "Done", documentType: "GEN", isFinal: true },
    ],
    transitions: [
      {
        key: "gen-complete",
        name: "Complete",
        documentType: "GEN",
        from: "requested",
        to: "done",
        executors: ["root"],
      },
    ],
    bikeModels: [
      {
        key: "model-1",
        model: "City Bike",
        code: "CB-01",
        customer: "test-customer",
      },
    ],
    bikes: [
      {
        model: "model-1",
        location: "test-location-1",
        serialNumber: "SN-TEST-001",
      },
    ],
    materials: [
      {
        sku: "MAT-TEST-001",
        name: "Brake Pad",
        unit: "unit",
        category: "parts",
        clientCost: 5,
        bikeModels: ["model-1"],
        customer: "test-customer",
      },
    ],
    documents: [
      {
        name: "Sample Request",
        documentType: "GEN",
        state: "requested",
        customer: "test-customer",
      },
    ],
  },
};
